import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AgreementItem, AttachmentTypeItem } from 'src/app/api/models';
import {
  AgreementsService,
  CommonService,
  DeliveryService,
  OrderService,
  TicketStatusService,
} from 'src/app/api/services';
import { environment } from 'src/environments/environment';

export type EmailNotificationMacroArea = 'DDT' | 'CUSTOMER_ORDER' | 'DELIVERY' | 'TICKET';

// id dello StatusWorkflowType lato BE per ciascuna macroarea (usato per il catalogo allegati
// e per identificare la macroarea nelle risposte dell'endpoint notification-configs)
export const MACRO_AREA_WORKFLOW_TYPE: Record<EmailNotificationMacroArea, number> = {
  TICKET: 2,
  CUSTOMER_ORDER: 3,
  DELIVERY: 4,
  DDT: 5,
};

const MACRO_AREA_LABELS: Record<EmailNotificationMacroArea, string> = {
  DDT: 'DDT',
  CUSTOMER_ORDER: 'Ordini cliente',
  DELIVERY: 'Consegne',
  TICKET: 'Ticket',
};

export interface EmailNotificationConfigItem {
  id: number;
  agreement: AgreementItem;
  macroArea: EmailNotificationMacroArea;
  statusIds: number[];
  statusLabels: string[];
  attachmentConfigIds: number[];
  attachmentLabels: string[];
}

export interface EmailNotificationConfigDialogData {
  registryEmailId: number;
  email: string;
  label?: string;
  existingConfig?: EmailNotificationConfigItem;
}

// Converte la risposta grezza del BE (snake_case) nella forma usata dal FE.
export function mapNotificationConfigResponse(data: any): EmailNotificationConfigItem {
  const macroArea = (Object.keys(MACRO_AREA_WORKFLOW_TYPE) as EmailNotificationMacroArea[]).find(
    key => MACRO_AREA_WORKFLOW_TYPE[key] === data.status_workflow_type_id
  ) as EmailNotificationMacroArea;
  return {
    id: data.id,
    agreement: data.agreement,
    macroArea,
    statusIds: data.status_ids ?? [],
    statusLabels: data.status_labels ?? [],
    attachmentConfigIds: data.attachment_config_ids ?? [],
    attachmentLabels: data.attachment_labels ?? [],
  };
}

interface StatusOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-modal-email-notification-config',
  templateUrl: './modal-email-notification-config.component.html',
  styleUrls: ['./modal-email-notification-config.component.scss'],
})
export class ModalEmailNotificationConfigComponent implements OnInit {
  data: EmailNotificationConfigDialogData;

  currentStep = 0;
  steps: MenuItem[] = [
    { label: 'Commessa' },
    { label: 'Macroarea' },
    { label: 'Stati' },
    { label: 'Allegati' },
  ];

  macroAreas: { value: EmailNotificationMacroArea; label: string; icon: string }[] = [
    { value: 'DDT', label: 'DDT', icon: 'pi pi-file' },
    { value: 'CUSTOMER_ORDER', label: 'Ordini cliente', icon: 'pi pi-shopping-cart' },
    { value: 'DELIVERY', label: 'Consegne', icon: 'pi pi-truck' },
    { value: 'TICKET', label: 'Ticket', icon: 'pi pi-ticket' },
  ];

  selectedAgreement?: AgreementItem;
  suggestedAgreements: AgreementItem[] = [];

  selectedMacroArea?: EmailNotificationMacroArea;

  availableStatuses: StatusOption[] = [];
  selectedStatusIds: number[] = [];
  isLoadingStatuses = false;

  availableAttachments: AttachmentTypeItem[] = [];
  selectedAttachmentIds: number[] = [];
  isLoadingAttachments = false;

  isSaving = false;

  constructor(
    private readonly dialogConfig: DynamicDialogConfig,
    public readonly dialogRef: DynamicDialogRef,
    private readonly agreementService: AgreementsService,
    private readonly ticketStatusService: TicketStatusService,
    private readonly deliveryService: DeliveryService,
    private readonly orderService: OrderService,
    private readonly commonService: CommonService,
    private readonly http: HttpClient,
    private readonly messageService: MessageService
  ) {
    this.data = this.dialogConfig.data as EmailNotificationConfigDialogData;
  }

  ngOnInit() {
    const existing = this.data.existingConfig;
    if (existing) {
      this.selectedAgreement = existing.agreement;
      this.selectedMacroArea = existing.macroArea;
      this.selectedStatusIds = [...existing.statusIds];
      this.selectedAttachmentIds = [...existing.attachmentConfigIds];
      if (!this.isDdt) {
        this.loadStatuses();
      }
      this.loadAttachments();
    }
  }

  get isDdt(): boolean {
    return this.selectedMacroArea === 'DDT';
  }

  filterAgreements(event: AutoCompleteCompleteEvent) {
    this.agreementService
      .agreementsControllerGetAll({ limit: 15, skip: 0, name: event.query, active: true })
      .subscribe(res => (this.suggestedAgreements = res.data.list));
  }

  selectMacroArea(area: EmailNotificationMacroArea) {
    if (this.selectedMacroArea === area) return;
    this.selectedMacroArea = area;
    this.selectedStatusIds = [];
    this.availableStatuses = [];
    this.selectedAttachmentIds = [];
    this.availableAttachments = [];
  }

  macroAreaLabel(area: EmailNotificationMacroArea): string {
    return MACRO_AREA_LABELS[area];
  }

  get canGoNext(): boolean {
    switch (this.currentStep) {
      case 0:
        return !!this.selectedAgreement;
      case 1:
        return !!this.selectedMacroArea;
      case 2:
        return this.selectedStatusIds.length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  }

  next() {
    if (!this.canGoNext) return;
    if (this.currentStep === 1) {
      if (this.isDdt) {
        this.loadAttachments();
        this.currentStep = 3;
        return;
      }
      this.loadStatuses();
    }
    if (this.currentStep === 2) {
      this.loadAttachments();
    }
    this.currentStep++;
  }

  back() {
    if (this.currentStep === 3 && this.isDdt) {
      this.currentStep = 1;
      return;
    }
    this.currentStep--;
  }

  private loadStatuses() {
    if (!this.selectedMacroArea) return;
    this.isLoadingStatuses = true;
    this.availableStatuses = [];
    switch (this.selectedMacroArea) {
      case 'TICKET':
        this.ticketStatusService
          .ticketStatusControllerFindAll({ limit: 0, skip: 0, visible: true })
          .subscribe({
            next: res => {
              this.availableStatuses = res.data.list.map(s => ({
                id: s.id,
                label: s.description,
              }));
              this.isLoadingStatuses = false;
            },
            error: () => (this.isLoadingStatuses = false),
          });
        break;
      case 'DELIVERY':
        this.deliveryService
          .deliveryStatusControllerFindAll({ limit: 200, skip: 0 })
          .subscribe({
            next: res => {
              this.availableStatuses = res.data.list.map(s => ({
                id: s.id,
                label: s.description,
              }));
              this.isLoadingStatuses = false;
            },
            error: () => (this.isLoadingStatuses = false),
          });
        break;
      case 'CUSTOMER_ORDER':
        this.orderService.customerOrderStatusControllerFindAll({ limit: 200 }).subscribe({
          next: res => {
            this.availableStatuses = res.data.items.map(s => ({ id: s.id, label: s.label }));
            this.isLoadingStatuses = false;
          },
          error: () => (this.isLoadingStatuses = false),
        });
        break;
    }
  }

  private loadAttachments() {
    if (!this.selectedMacroArea) return;
    this.isLoadingAttachments = true;
    const type = MACRO_AREA_WORKFLOW_TYPE[this.selectedMacroArea];
    this.commonService.commonControllerGetAttachmentTypes({ type }).subscribe({
      next: res => {
        this.availableAttachments = res.data.list || [];
        this.isLoadingAttachments = false;
      },
      error: () => (this.isLoadingAttachments = false),
    });
  }

  save() {
    if (!this.selectedAgreement || !this.selectedMacroArea) return;
    this.isSaving = true;

    const payload = {
      agreement_id: this.selectedAgreement.id,
      status_workflow_type_id: MACRO_AREA_WORKFLOW_TYPE[this.selectedMacroArea],
      status_ids: [...this.selectedStatusIds],
      attachment_config_ids: [...this.selectedAttachmentIds],
    };
    const base = `${environment.baseUrl}/registry-emails/${this.data.registryEmailId}/notification-configs`;
    const request$ = this.data.existingConfig
      ? this.http.put<any>(`${base}/${this.data.existingConfig.id}`, payload)
      : this.http.post<any>(base, payload);

    request$.subscribe({
      next: res => {
        this.isSaving = false;
        this.dialogRef.close(mapNotificationConfigResponse(res.data));
      },
      error: err => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: err?.error?.message || 'Errore durante il salvataggio della configurazione.',
        });
      },
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
